<template>
  <div class="delivery-details-table">
    <table class="table is-fullwidth">
      <tr v-if="orderID">
        <td class="key is-narrow">View</td>
        <td>
          <span class="link" @click="goto(`commerce_order/${orderID}`)">
            <!-- Customer View -->
            Order #{{ t.data.ref }}
          </span>
          <span class="has-text-info is-pulled-right" @click="goto(`commerce_agent_order/${orderID}`)">
            Agent View <i class="fa fa-external-link-alt" />
          </span>
        </td>
      </tr>
      <tr>
        <td class="key is-narrow">Customer</td>
        <td>
          <i class="fa fa-phone is-pulled-right" @click="showCustomerPhone = !showCustomerPhone" />
          <p class="single-line">{{ customer }}</p>
          <p v-if="showCustomerPhone">
            <span class="link" @click="callPhoneNumber(t.customer.phone_number)">
              {{ t.customer.phone_number }}
            </span>
          </p>
          <p v-if="t.data.gift_details" class="has-text-danger"
            @click="callPhoneNumber(t.data.gift_details.recipient_phone)">
            <i class="fa fa-gift" />&nbsp;Gift Recipient:
            {{ t.data.gift_details.recipient_phone }}
            <i class="fa fa-phone" />
          </p>
        </td>
      </tr>
      <tr>
        <td class="key">Pickup</td>
        <td>
          {{ d.pickup_stop.name || "Pin Location" }}
          <i class="fa fa-map-marker-alt is-pulled-right" @click="showPickupCoords = !showPickupCoords" />
          <p v-if="showPickupCoords">
            <span class="link" @click="openMap(d.pickup_stop)">
              {{ `${d.pickup_stop.lat}, ${d.pickup_stop.lon}` }}
            </span>
          </p>
        </td>
      </tr>
      <tr>
        <td class="key">Dropoff</td>
        <td>
          {{ d.dropoff_stop.name || "Pin Location" }}
          <i class="fa fa-map-marker-alt is-pulled-right" @click="showDropoffCoords = !showDropoffCoords" />
          <p v-if="showDropoffCoords">
            <span class="link" @click="openMap(d.dropoff_stop)">
              {{ `${d.dropoff_stop.lat}, ${d.dropoff_stop.lon}` }}
            </span>
          </p>
          <p v-if="d.coords_alighted" :class="dropoffToAlightedDistance > 50
            ? 'has-text-danger'
            : 'has-text-primary'
            ">
            Alighted {{ dropoffToAlightedDistance }}m away
          </p>
        </td>
      </tr>
      <tr>
        <td class="key">Fare</td>
        <td>
          <span @click="editTukPay">{{
            `฿${d.fare.price} (${d.fare.cash} cash)`
          }}</span>
          <span class="is-pulled-right">
            <span class="link has-text-info" style="margin-right: 8px" @click="openShortestRoute">{{
              d.fare.distance
            }}m</span>

            <i class="fa fa-route" @click="openGoogleRoute" />
          </span>
        </td>
      </tr>
      <tr>
        <td class="key">Remit</td>
        <td class="has-text-success">
          <span>
            <i class="fa fa-shipping-fast fa-sm" />&nbsp;{{
              `฿${d.fare.remit || 0}`
            }}</span>
          &nbsp;
          <span>
            <i class="fa fa-store-alt fa-sm" />&nbsp;{{
              `฿${d.delivery.remit || 0}`
            }}</span>
        </td>
      </tr>
      <tr>
        <td class="key">Order</td>
        <td>
          {{ `฿${d.delivery.order_value} ${paymentMethod}` }}
          <span class="tag is-pulled-right">
            <!-- Collect ฿{{ d.delivery.order_value + d.fare.cash }} -->
            Collect ฿{{ d.delivery.order_value + clientCash }}
          </span>
        </td>
      </tr>
      <tr v-if="d.delivery.cashback">
        <td class="key has-text-warning">Cashback</td>
        <td class="has-text-warning">
          {{ `฿${d.delivery.cashback}` }}
        </td>
      </tr>
      <tr>
        <td class="key">Created</td>
        <td>
          {{ new Date(t.created_at).toLocaleTimeString("en-GB") }}
          <span class="tag is-pulled-right">
            Updated {{ timeAgo(t.updated_at) }}
          </span>
        </td>
      </tr>

      <tr @click="showFleetOptions = !showFleetOptions">
        <td class="key">Fleet</td>
        <td>
          <p>
            {{ expressFleet }}
            <i class="fa fa-chevron-down is-pulled-right" />
          </p>
          <p class="has-text-grey">{{ fallbackFleet }}</p>
          <!-- <span v-if="fallbackFleet" class="tag is-pulled-right">
          {{ shortenString(fallbackFleet, 15) }}
        </span> -->
        </td>
      </tr>

      <tr v-if="closestDrivers" style="background-color: #00e">
        <td colspan="2">
          <p class="key" @click="closestDrivers = null">
            Closest Drivers
            <i class="fa fa-times-circle fa-lg is-pulled-right" />
          </p>
          <p v-if="!closestDrivers.length">
            No nearby drivers currently enroute
          </p>
          <p v-for="c in closestDrivers" :key="c.user_id" style="margin-top: 6px" @click="assignClosestDriver(c)">
            <span class="tag is-dark">{{ c.duration }} mins</span>&nbsp;
            <span>{{ c.name }}</span>
            <i class="fa fa-chevron-right is-pulled-right" />
          </p>
        </td>
      </tr>

      <tr v-if="showFleetOptions" style="background-color: black">
        <td colspan="2">
          <p>Fleet Options</p>
          <p class="buttons" style="margin-bottom: 0">
            <!-- <span class="key">Options</span>&nbsp; -->
            <span class="button is-white" @click="changeFleet">Change Fleet</span>
            <span class="button is-info" @click="getClosestDeliveryDrivers">Closest Drivers</span>
          </p>
          <p>Lalamove</p>
          <p class="buttons">
            <!-- <span class="key">Lalamove</span>&nbsp; -->
            <span v-if="t.state === 'initiated'" class="button is-info" @click="addTip">Add Tip
              <span v-if="lalamoveTip" style="margin-left: 4px">({{ lalamoveTip }})</span>
            </span>
            <span class="button is-primary" @click="cancelAndRetry">Cancel and Retry</span>
            <span class="button is-warning" @click="cancelDelivery">Cancel</span>
            <span class="button is-danger" @click="changeLalamoveOrderID">Change Lalamove Order ID</span>
          </p>
          <!-- <p v-if="lalamoveTip">
          <span class="tag">Current Tip: {{ lalamoveTip }} </span>
        </p> -->
        </td>
      </tr>

      <tr v-if="lalamoveShareLink">
        <td class="key">Lalamove</td>
        <td>
          <span class="link" @click="openLalamoveShareLink">
            Share Link&nbsp;<i class="fa fa-mobile-alt" />
          </span>
          <span class="link is-pulled-right" @click="openLalamoveDashboard">
            Dashboard&nbsp;<i class="fa fa-desktop" />
          </span>
          <!-- <i class="fa fa-external-link-alt is-pulled-right" /> -->
        </td>
      </tr>

      <tr @click="showEvents">
        <td class="key">Events</td>
        <td>
          {{ toConfirmed }} - {{ toArrived }} - {{ toEnroute }} -
          {{ toAlighted }}
          <!-- to_confirmed, to_arrived, to_enroute, to_alighted -->
          <i class="fa fa-info-circle is-pulled-right" />
        </td>
      </tr>

      <tr v-if="driversAttempted.length">
        <td class="key">Drivers</td>
        <td>
          <span v-for="d in driversAttempted" :key="d" class="tag is-light"
            :class="{ 'has-text-danger': !t.driver || t.driver.id !== d }" style="margin-right: 3px"
            @click="copyDriverID(d)">{{ shortenString(d, 6) }}</span>
        </td>
      </tr>
      <tr>
        <td class="key">Driver</td>
        <td>
          <div v-if="t.driver">
            <i class="fa fa-phone is-pulled-right" @click="showDriverPhone = !showDriverPhone" />
            <p class="single-line">
              <span class="link" @click="toggleDriverList">{{
                t.driver.name
              }}</span>
            </p>

            <p v-if="showDriverPhone">
              <span class="link" @click="callPhoneNumber(t.driver.phone_number)">
                {{ t.driver.phone_number }}
              </span>
            </p>
          </div>
          <div v-else>
            <i class="link" @click="toggleDriverList"> Assign driver </i>
            <span class="tag is-pulled-right is-danger">No driver yet</span>
          </div>
        </td>
      </tr>
    </table>

    <!---------------------- change driver ------------------------>

    <div v-if="showDriverList">
      <div class="change-driver-background" @click="showDriverList = false" />
      <div class="change-driver-wrapper">
        <table class="table is-fullwidth driver-table no-scrollbar">
          <tr class="driver-list">
            <td>
              <div style="max-height:calc(80vh - 250px); overflow:scroll">
                <p v-for="d in filterDrivers(combinedDrivers)" :key="d.key" @click="preChangeDriver(d)"
                  class="single-line no-scrollbar 100vw">
                  {{ d.key }}
                </p>
              </div>
              <div v-if="!drivers.length" style="opacity: 0.6">
                <!-- <b-skeleton height="36px" />
                <b-skeleton height="36px" />
                <b-skeleton height="36px" /> -->
                <p><i class="fa fa-spinner fa-spin" />&nbsp;Fetching drivers</p>
              </div>
              <div style="margin: 10px">
                <input type="text" autocomplete="new-password" class="input is-medium" v-model="searchDriver"
                  @keyup="setSearchDriver($event)" placeholder="🔍 Search Driver" />
              </div>
            </td>
          </tr>
          <!-- <tr>
            <td class="is-flex">
              <p class="button is-fullwidth is-info" @click="preAssignAdminDriver">
                <i class="fa fa-briefcase" />&nbsp;Assign Admin
              </p>&nbsp;
              <p class="button is-fullwidth is-warning" @click="preUnassignDriver">
                <i class="fa fa-minus-circle" />&nbsp;Un-assign driver
              </p>
            </td>
          </tr> -->
          <tr>
            <td class="is-flex" style="gap: 4px">
              <p v-for="admin in adminDrivers" :key="admin.id" class="button is-fullwidth is-dark"
                style="border:1px solid grey" @click="preAssignAdminDriver(admin)">
                {{ admin.name }}
              </p>
            </td>
          </tr>
          <tr>
            <td>
              <div v-if="t.driver" class="is-flex">
                <p style="flex:1" class="button is-dark has-text-info">
                  {{ shortenString(t.driver.name, 12) }}&nbsp;
                  <span class="link" @click="callPhoneNumber(t.driver.phone_number)">
                    {{ t.driver.phone_number }}
                  </span>
                </p>
                <p class="button is-danger" @click="preUnassignDriver">
                  <i class="fa fa-minus-circle" />&nbsp;Unassign
                </p>
              </div>
              <div v-else class="has-text-centered has-text-danger">
                No driver yet
              </div>
            </td>
          </tr>
        </table>
        <p style="text-align:center; color:white; margin:18px" @click="showDriverList = false">
          <i class="fa fa-chevron-down fa-lg" />
        </p>
      </div>
    </div>
  </div>
</template>


<script>
// import DeliveryDetailsTable from "@/components/DeliveryDetailsTable.vue";

import geo from "@/geo";

export default {
  name: "DeliveryDetailsTable",
  props: {
    t: Object, // transaction
  },
  data() {
    return {
      searchDriver: null,
      showCustomerPhone: false,
      showDriverPhone: false,
      showPickupCoords: false,
      showDropoffCoords: false,
      showDriverList: false,
      drivers: [],
      fallbackDrivers: [],
      showFleetOptions: false,
      closestDrivers: null,
      adminDrivers: [
        //  { name: 'Nick', id: '7af535e6-ffea-11e9-aa6d-76d38a004a34' }, // Nick Staging
        { name: 'Mint', id: '26ba9921-6f9a-11eb-a3cb-96d83c7bed1c' },
        { name: 'Nick', id: 'd1e97b9e-0177-11ea-b3dc-2e31b56cd9db' },
        { name: 'Artchi', id: '4e5270b7-2e91-11ee-9eb3-9e29d7144947' }
        // { name: 'Krit', id: 'bf0fe93a-061e-11eb-ad0b-06d5dd50d127' }
      ],
    };
  },
  computed: {
    combinedDrivers() {
      // combine drivers and fallbackDrivers and de-dupe
      var ids = new Set(this.drivers.map(d => d.value)); // driver ID
      var combined = [...this.drivers, ...this.fallbackDrivers.filter(d => !ids.has(d.value))];
      combined.sort((a, b) => a.key.localeCompare(b.key)) // then sort alphabetical
      return combined
    },
    driversAttempted() {
      if (!this.d.drivers) return [];
      return this.d.drivers.split(",");
    },
    d() {
      return this.t.data;
    },
    clientCash() {
      if (!this.d.fare) return 0;
      return this.d.fare.client || this.d.fare.cash; // for backward compatability
    },
    orderID() {
      return this.t.data.commerce_transaction_id;
    },
    lalamoveTip() {
      return this.d.lalamove_tip_amount;
    },
    tukpay() {
      return this.d.fare.tukpay;
    },
    lalamoveShareLink() {
      return this.d.lalamove_share_link;
    },
    customer() {
      return this.t.customer.name || "Unknown " + this.t.customer.phone_number;
    },
    paymentMethod() {
      var pm = this.d.delivery.payment_method;
      return this.translatePayment(pm).toUpperCase();
    },
    expressFleet() {
      return (
        this.t.data.delivery.express_fleet || this.lastFive(this.t.workflow_id)
      );
    },
    fallbackFleet() {
      return this.t.data.delivery.fallback_fleet;
    },
    fallbackWorkflowID() {
      return this.t.data.fallback_workflow_id;
    },
    toConfirmed() {
      var a = this.d.event_created || this.t.created_at;
      var b = this.d.event_confirmed;
      return this.timeDifferenceMinutes(a, b);
    },
    toArrived() {
      var a = this.d.event_confirmed;
      var b = this.d.event_driver_arrived;
      return this.timeDifferenceMinutes(a, b);
    },
    toEnroute() {
      var a = this.d.event_driver_arrived;
      var b = this.d.event_enroute;
      return this.timeDifferenceMinutes(a, b);
    },
    toAlighted() {
      var a = this.d.event_enroute;
      var b = this.d.event_alighted;
      return this.timeDifferenceMinutes(a, b);
    },
    dropoffToAlightedDistance() {
      var a = this.d.dropoff_stop;
      var b = this.d.coords_alighted;
      return geo.distanceToUserInMeters(a, b);
    },
  },
  methods: {
    // gotoBusinessProfile() {
    //   this.goto("/business_profile/" + this.t.workflow_id);
    // },
    setSearchDriver(e) {
      this.searchDriver = e.target.value; // a hack for mobile...
    },
    filterDrivers(arr) {
      if (!this.searchDriver) return arr;
      return arr.filter((d) => {
        var name = d.key.toLowerCase();
        return name.includes(this.searchDriver.toLowerCase());
      });
    },
    assignClosestDriver(c) {
      console.log("assignClosestDriver", c);
      this.infoToast("Manually assign driver below<br>the usual way");
    },
    editTukPay() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'><i class='fa fa-coins'></i>&nbsp;Add TUKPay<p/>
        <p style='margin-bottom:10px'>This edits the TUKPay.</p>
        <p><i>Current TUKPay: <b>${this.tukpay}</b></i></p>
        </div>`,
        input: "text",
        inputPlaceholder: "฿10",
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: "Add TUKPay",
        inputValidator: (val) => {
          if (!val || parseInt(val) < this.tukpay)
            return "Must be greater than " + this.tukpay;
        },
        inputAttributes: { autocomplete: "nope" },
        // didOpen: () => (this.$swal.getInput().autocomplete = "nope"),
      }).then((val) => {
        if (!val.value) return;
        var tukpay = parseInt(val.value);
        this.infoToast("Setting TUKPay to ฿" + tukpay);
        var data = {
          id: this.t.id,
          tukpay: tukpay,
        };
        var loader = this.loadingDots("orange");
        this.$store
          .dispatch("addTukpayToDelivery", data)
          .then(() => this.successToast("Success!"))
          .catch((err) => this.apiError(err))
          .finally(() => loader.hide());
      });
    },
    changeFleet() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'><i class='fa fa-motorcycle'></i>&nbsp;Change Express Fleet</p>
        <p>Enter the exact name of the fleet or choose from the options in the error response.</p>
        <p style='font-size:2em'>TODO</p>
        </div>`,
        showCloseButton: true,
        input: "text",
      });
    },
    getClosestDeliveryDrivers() {
      this.closestDrivers = null;
      var loader = this.loadingDots("lime");
      this.$store
        .dispatch("getClosestDeliveryDrivers", this.t.id)
        .then((res) => {
          this.closestDrivers = res.data;
        })
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
    addTip() {
      var currentTip = "";
      if (this.lalamoveTip)
        currentTip = `<br><p><i>Current tip: ${this.lalamoveTip}</i></p>`;
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'><i class='fa fa-coins'></i>&nbsp;Add a tip<p/>
        <p>For now only works for lalamove tips, but we could add to tukpay as well.</p>
        ${currentTip}
        </div>`,
        input: "text",
        inputPlaceholder: "฿10",
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: "Add Tip",
        inputValidator: (val) => {
          if (!val || parseInt(val) < this.lalamoveTip)
            return "Must be greater than " + this.lalamoveTip;
        },
      }).then((val) => {
        if (val.value) {
          var intTip = parseInt(val.value);
          this.infoToast("Adding Tip ฿" + intTip);
          var data = {
            id: this.t.id,
            tip: intTip,
          };
          var loader = this.loadingDots("orange");
          this.$store
            .dispatch("addTipToDelivery", data)
            .then(() => this.successToast("Success!"))
            .catch((err) => this.apiError(err))
            .finally(() => loader.hide());
        }
      });
    },
    changeLalamoveOrderID() {
      //manually change the lalamove order ID
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:12px'>Change Lalamove Order ID</p>
        <p>Current Lalamove Order ID:</p>
        <p>[ <b>${this.t.data.lalamove_order_id}</b> ]</p>
        </div>`,
        showCloseButton: true,
        confirmButtonText: "Submit",
        input: "number",
        inputPlaceholder: "106637819641",
        inputValidator: (val) => {
          if (!val || val.length < 12) return "Should be 12 digits";
        },
      }).then((val) => {
        if (val.value) {
          this.$store
            .dispatch("patchTransaction", {
              transactionID: this.t.id,
              data: {
                data: {
                  lalamove_order_id: val.value,
                },
              },
            })
            .then(() => this.successToast("Lalamove order ID updated!"))
            .catch((err) => this.apiError(err));
        }
      });
    },
    cancelDelivery() {
      var data = {
        transaction_id: this.t.id,
        lalamove_order_id: this.t.data.lalamove_order_id,
      };
      var loader = this.loadingDots();
      this.$store
        .dispatch("cancelLalamoveOrder", data)
        .then(() => this.successToast("Success"))
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
    cancelAndRetry() {
      var data = {
        transaction_id: this.t.id,
        lalamove_order_id: this.t.data.lalamove_order_id,
      };
      var loader = this.loadingDots();
      this.$store
        .dispatch("retryLalamoveOrder", data)
        .then(() => this.successToast("Success"))
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
    openLalamoveShareLink() {
      window.open(this.lalamoveShareLink, "_target");
    },
    openLalamoveDashboard() {
      var url = `https://web.lalamove.com/orders?max=10&start=2021-05-10&end=2021-07-09`;
      window.open(url, "_target");
    },
    showEvents() {
      var createdAt = new Date(this.t.created_at).toLocaleTimeString("en-GB");
      var startedAt = this.t.data.event_created; // actual createdAt of original delivery
      if (startedAt) {
        createdAt = new Date(startedAt).toLocaleTimeString("en-GB");
      }
      var alightedAt = this.t.data.event_alighted;
      var updatedAt = new Date(this.t.updated_at).toLocaleTimeString("en-GB");
      if (alightedAt) {
        updatedAt = new Date(alightedAt).toLocaleTimeString("en-GB");
      }
      var createdAgo = this.timeAgo(this.t.created_at);
      var updatedAgo = this.timeAgo(this.t.updated_at);
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em; margin-bottom:10px'>Delivery Events</p>
        <p style='margin-bottom:4px'>Created 
        <span style='float:right'><code style='font-size:0.8em'>${createdAgo}</code><button>${createdAt}</button>
        </p>
        <p style='margin-bottom:4px'>Created - Confirmed
        <span style='float:right'>${this.toConfirmed} mins</span>
        </p>
        <p style='margin-bottom:4px'>Confirmed - Arrived
        <span style='float:right'>${this.toArrived} mins</span>
        </p>
        <p style='margin-bottom:4px'>Arrived - Enroute
        <span style='float:right'>${this.toEnroute} mins</span>
        </p>
        <p style='margin-bottom:4px'>Enroute - Alighted
        <span style='float:right'>${this.toAlighted} mins</span>
        </p>
        <p style='margin-bottom:4px'>Updated 
        <span style='float:right'><code style='font-size:0.8em'>${updatedAgo}</code><button>${updatedAt}</button></span>
        </p>
        </div>`,
        showCloseButton: true,
        showConfirmButton: false,
      });
    },
    timeDifferenceMinutes(a, b) {
      if (!a || !b) return "⏱";
      return Math.round((new Date(b) - new Date(a)) / (1000 * 60));
    },
    lastFive(id) {
      return id.substr(id.length - 5);
    },
    toggleDriverList() {
      this.showDriverList = !this.showDriverList;
      if (!this.showDriverList) return;
      // update driver list
      this.drivers = [];
      console.log("autocompleteDeliveryDrivers", this.t.workflow_id);
      this.$store
        .dispatch("autocompleteDeliveryDrivers", this.t.workflow_id)
        .then((data) => {
          if (data) this.drivers = data;
          else this.drivers = [];
          // this.$refs.scrollable.scrollBy(0, -100); // scrollable is in parent component
        })
        .catch((err) => this.apiError(err));
      if (this.fallbackWorkflowID) this.getFallbackDeliveryDrivers();
    },
    getFallbackDeliveryDrivers() {
      this.fallbackDrivers = [];
      this.$store
        .dispatch("autocompleteDeliveryDrivers", this.fallbackWorkflowID)
        .then((data) => {
          if (data) this.fallbackDrivers = data;
          else this.fallbackDrivers = [];
        })
        .catch((err) => this.apiError(err));
    },
    preAssignAdminDriver(admin) {
      this.infoToast(`Assigning admin: ${admin.name} ${admin.id}`, "top")
      var msg = `👨🏻‍🚀[${this.$store.getters.realName}] assigned delivery to 🛵[ ${admin.name} ]`;
      this.sendSuperuserNotification(msg);
      this.changeDriver(admin.id);
    },
    preUnassignDriver() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='margin-bottom:12px; font-size:1.1em'>
        <i class='fa fa-minus-circle'></i>&nbsp;Un-assign Driver</p>
        <p style='margin-bottom:9px'>Are you sure you want to un-assign the driver from this delivery?</p>
        <p>The job will  be sent back out to the driver pool</p>
        </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Un-assign",
      }).then((val) => {
        if (val.value) this.unassignDriver();
      });
    },
    unassignDriver() {
      var loader = this.loadingDots();
      this.$store
        .dispatch("patchTransaction", {
          transactionID: this.t.id,
          data: {
            state: "unassign", //"initiated",
            // handled_by: "",
            data: { acknowledged: false },
          },
        })
        .then(() =>
          this.successToast("Unassigned driver, reload to check", "top")
        )
        .catch((err) => this.apiError(err))
        .finally(() => {
          loader.hide();
          this.showDriverList = false;
        });
    },
    preChangeDriver(d) {
      this.$swal({
        html: `<div style='text-align:left'>
<p style='font-weight:500; font-size:1.2em; opacity:0.7; margin-bottom:10px'>Change driver to:</p>
<p>${d.key}</p>
</div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes, Change",
        confirmButtonColor: this.bulmaColor("primary"),
      }).then((val) => {
        if (val.value) {
          var msg = `👨🏻‍🚀[${this.$store.getters.realName}] assigned delivery to 🛵[ ${d.key} ]`;
          this.sendSuperuserNotification(msg);
          this.changeDriver(d.value);
        }
      });
    },
    changeDriver(driverID) {
      console.log("changeDriver", driverID);
      var loader = this.loadingDots();
      this.$store
        .dispatch("patchTransaction", {
          transactionID: this.t.id,
          data: {
            state: "confirmed",
            handled_by: driverID,
            data: { acknowledged: false },
          },
        })
        .then(() => this.successToast("Changed driver, reload to check", "top"))
        .catch((err) => this.apiError(err))
        .finally(() => {
          loader.hide();
          this.showDriverList = false;
        });
    },
    openMap(coords) {
      var url = `http://www.google.com/maps/place/${coords.lat},${coords.lon}`;
      window.open(url, "_blank");
    },
    openGoogleRoute() {
      var scoords = `${this.d.pickup_stop.lat},${this.d.pickup_stop.lon}`;
      var dcoords = `${this.d.dropoff_stop.lat},${this.d.dropoff_stop.lon}`;
      var url = `https://www.google.com/maps?saddr=${scoords}&daddr=${dcoords}`;
      window.open(url, "_blank");
    },
    openShortestRoute() {
      this.goto("/driver_route/" + this.orderID);
    },
    // coords info
    showMoreInfo(key) {
      var data = this.t.data;
      if (key === "Fare") {
        if (data.pickup_stop && data.dropoff_stop) {
          this.mapURL = this.directionsURL(data.pickup_stop, data.dropoff_stop);
        } else if (data.address && data.business) {
          this.mapURL = this.directionsURL(data.business, data.address);
        } else return;
        this.moreInfo = "View driving route";
        return;
      }
      // just show coords and link to google location
      var p = null;
      if (key === "Pickup") {
        p = data.pickup_stop;
        this.moreInfo = `Pickup: ${p.lat}, ${p.lon}`;
      }
      if (key === "Dropoff") {
        p = data.dropoff_stop;
        this.moreInfo = `Dropoff: ${p.lat}, ${p.lon}`;
      }
      if (key === "Shop") {
        p = data.business;
        this.moreInfo = `Shop: ${p.lat}, ${p.lon}`;
      }
      if (key === "Address") {
        p = data.address;
        this.moreInfo = `Address: ${p.lat}, ${p.lon}`;
      }
      if (p) this.mapURL = this.googleMapURL(p);
    },
    directionsURL(s, d) {
      var scoords = `${s.lat},${s.lon}`;
      var dcoords = `${d.lat},${d.lon}`;
      return `https://www.google.com/maps?saddr=${scoords}&daddr=${dcoords}`;
    },
    googleMapURL(coords) {
      return `http://www.google.com/maps/place/${coords.lat},${coords.lon}`;
    },
    gotoMapURL() {
      window.open(this.mapURL, "_blank");
    },
    copyDriverID(driverID) {
      this.$copyText(driverID);
      this.infoToast("Copied driver ID<br>" + driverID);
      setTimeout(() => this.goto(`user_info/${driverID}`), 400);
    },
    callPhone(e) {
      var val = e.target.innerText;
      if (val === "Customer") {
        this.calling = "Customer: " + this.t.customer.phone_number;
        this.callPhoneNumber(this.t.customer.phone_number);
      }
      if (val === "Driver") {
        this.calling = "Driver: " + this.t.driver.phone_number;
        this.callPhoneNumber(this.t.driver.phone_number);
      }
      if (val === "Shop") {
        this.goto(`/shop/${this.t.business_id}?tab=info`);
        this.calling = "from shop profile...";
      }
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow: scroll;
  max-height: calc(100vh - 85px);
  /* max-width: 100vw; */
}

.table {
  background-color: #333;
  color: snow;
}

.table td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.3);
}

.calling {
  /* color: yellow; */
  margin-bottom: 10px;
  background-color: #ffdd57;
  padding: 4px;
}

.key {
  font-weight: 550;
  /* color: rgba(84, 84, 84, 0.671); */
  color: rgba(255, 250, 250, 0.3);
  padding-right: 0;
  /* overflow-wrap: none; */
  white-space: nowrap;
}

td {
  /* max-width: calc(100); */
  overflow-wrap: anywhere;
  padding: 8px 10px;
}

.action-btns {
  margin-bottom: 0;
  position: sticky;
  bottom: 0px;
  backdrop-filter: blur(2px);
}

.action-btns .button {
  padding: 9px;
}

.tag {
  padding: 4px;
}

.fa-external-link-alt {
  padding: 4px;
}

.fa {
  padding: 4px;
}

.driver-list td {
  padding: 0;
  width: 100vw;
}

.driver-list p {
  background-color: #666;
  margin: 2px;
  padding: 8px 10px;
  max-width: calc(100vw - 4px);
}

.single-line {
  white-space: nowrap;
  overflow: auto;
  /* max-width: calc(100vw - 170px); */
  text-overflow: ellipsis;
}

.change-driver-background {
  position: absolute;
  bottom: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(3px);
}

.change-driver-wrapper {
  position: absolute;
  bottom: 0;
  /* max-height: 80vh; */
  width: 100vw;
  display: block;
  /* overflow: scroll; */
  z-index: 3;
  margin-bottom: 0;
  background-color: black;
}

.driver-table {
  /* position: absolute;
  bottom: 0;
  max-height: 80vh;
  width: 100vw;
  display: block;
  overflow: scroll;
  z-index: 3;
  */
  /* width: calc(100vw - 20px); */
  overflow: scroll;
  max-height: 80vh;
  margin-bottom: 0;
}
</style>
